/**
 * @module Components.SignUp
 *
 * In-component signup step shown to athletes / parents-fans after the
 * role/about step. Athletes invited by a coach join for free through the
 * invite, so we ask before sending anyone to a paid checkout:
 *   invited ("Yes") -> "Check with your coach" dead end
 *   on their own ("No") -> Analyze paywall (/trial/analyze)
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

export type InvitedAnswer = "yes" | "no";

type OptionDef = {
  value: InvitedAnswer;
  icon: string;
  title: string;
  description: string;
};

type PropsDef = {
  onAnswer: (answer: InvitedAnswer) => void;
  // Omitted when the step is resumed from a paywall — there is no earlier
  // step to return to, so the Back button is hidden.
  onBack?: () => void;
};

export default function InvitedGate(props: PropsDef) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<InvitedAnswer | null>(null);

  // Built inside the component so t() runs in a translation context.
  const options: OptionDef[] = [
    {
      value: "yes",
      icon: "✉️",
      title: t("Yes, my coach invited me"),
      description: t("I'll join through my coach's invite link."),
    },
    {
      value: "no",
      icon: "👤",
      title: t("No, I'm signing up on my own"),
      description: t("I'll set up my own Analyze account."),
    },
  ];

  const submit = () => {
    if (selected) {
      props.onAnswer(selected);
    }
  };

  return (
    <div className={styles.container}>
      {props.onBack && (
        <button
          type="button"
          className={styles.back}
          onClick={props.onBack}
          aria-label={t("Back")}
        >
          <i className="fas fa-arrow-left" aria-hidden="true" />
          {t("Back")}
        </button>
      )}

      <h2 className={styles.title}>{t("Were you invited by a coach?")}</h2>
      <p className={styles.subtitle}>
        {t(
          "Athletes working with a CoachNow coach can join for free through their coach's invite — no payment needed.",
        )}
      </p>

      <div className={styles.options}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cls(styles.option, {
              [styles.selected]: selected === option.value,
            })}
            onClick={() => setSelected(option.value)}
            aria-pressed={selected === option.value}
          >
            <span className={styles.option_icon}>{option.icon}</span>
            <span className={styles.option_title}>{option.title}</span>
            <span className={styles.option_description}>
              {option.description}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="btn btn-primary full"
        onClick={submit}
        disabled={!selected}
      >
        {t("Continue")}
      </button>
    </div>
  );
}
